// LD-4 — useLadderBudget hook.
//
// Reads the user's entertainment budget via get_ld_budget RPC.
// Returns monthly cap, current spend, remaining amount, pause state.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ────────────────────────────────────────────────

export interface LdBudget {
  id: string;
  monthly_cap: number;
  currency: string;
  current_period_start: string;
  current_period_spent: number;
  amount_remaining: number;
  is_paused: boolean;
}

export interface UseLadderBudgetState {
  budget: LdBudget | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Hook ─────────────────────────────────────────────────

export function useLadderBudget(): UseLadderBudgetState {
  const { user } = useAuthSession();
  const [budget, setBudget] = useState<LdBudget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setBudget(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { data, error: rpcError } = await (
          client.rpc as unknown as (fn: string) => Promise<{
            data: Record<string, unknown> | null;
            error: { message: string } | null;
          }>
        )("get_ld_budget");

        if (cancelled) return;

        if (rpcError) {
          setError(rpcError.message);
          setLoading(false);
          return;
        }

        if (!data) {
          setBudget(null);
          setLoading(false);
          return;
        }

        const raw = data as Record<string, unknown>;
        if (raw.budget && typeof raw.budget === "object") {
          const b = raw.budget as Record<string, unknown>;
          setBudget({
            id: String(b.id ?? ""),
            monthly_cap: Number(b.monthly_cap ?? 0),
            currency: String(b.currency ?? "GHS"),
            current_period_start: String(b.current_period_start ?? ""),
            current_period_spent: Number(b.current_period_spent ?? 0),
            amount_remaining: Number(b.amount_remaining ?? 0),
            is_paused: b.is_paused === true,
          });
        } else {
          setBudget(null);
        }

        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, tick]);

  return useMemo(
    () => ({ budget, loading, error, refetch }),
    [budget, loading, error, refetch],
  );
}
